// =============================================================================
// VandieZ agent-web -- Broker desk (/broker)
// =============================================================================
// WS-A. Four views on one page because they are one job: an agent deciding
// what to take, who to give it to, and whether it made money.
//
// VOCABULARY: "broker" is the FSD's *Agent* persona -- buys transport from a
// driver, sells it to a customer, keeps the margin. Our own `agent` role
// (acting for a vehicle owner) is a different concept that already shipped;
// D-4 kept our name and renamed the FSD's.
//
// Nothing here takes a broker id. Every endpoint is scoped server-side by the
// token, because brokers on this platform are direct competitors and a
// margin leak is a commercial disaster rather than a bug.
// =============================================================================

import {
  AlertTriangle,
  IndianRupee,
  Loader2,
  MapPin,
  Phone,
  RefreshCcw,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  BrokerDashboard,
  BrokerMarginsResponse,
  BrokerRosterResponse,
  NearbyVehiclesResponse,
  BrokerReportResponse,
} from "@/types/broker";
import { brokerApi } from "@/api/client";

type Tab = "overview" | "margins" | "drivers" | "reports" | "nearby";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "margins", label: "Margins" },
  { id: "drivers", label: "My drivers" },
  { id: "reports", label: "Reports" },
  { id: "nearby", label: "Find vehicles" },
];

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Renders a period-over-period delta.
 *
 * `null` means the prior period had no activity, and it renders as "first
 * period" rather than as a percentage. The backend is deliberate about
 * this: "up 100%" from a base of nothing reads as performance when it
 * means there is nothing to compare against.
 */
function DeltaBadge({ pct }: { pct: number | null }): React.JSX.Element {
  if (pct === null) {
    return <span className="text-xs text-muted-foreground">first period</span>;
  }
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        up ? "text-emerald-600" : "text-red-600"
      }`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {up ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
}

function rupees(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  const n = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(n)) {
    return "—";
  }
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export function BrokerPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<Tab>("overview");
  const [dashboard, setDashboard] = useState<BrokerDashboard | null>(null);
  const [margins, setMargins] = useState<BrokerMarginsResponse | null>(null);
  const [roster, setRoster] = useState<BrokerRosterResponse | null>(null);
  const [nearby, setNearby] = useState<NearbyVehiclesResponse | null>(null);
  const [report, setReport] = useState<BrokerReportResponse | null>(null);
  // A trailing 30 days. The server derives the comparison window itself —
  // same length, ending where this one begins — so there is no second
  // pair of dates to get wrong.
  const [reportDays, setReportDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chennai, as a starting point for the vehicle search. A real deployment
  // would seed this from the load being planned.
  const [lat, setLat] = useState("13.0827");
  const [lng, setLng] = useState("80.2707");
  const [radiusKm, setRadiusKm] = useState("100");

  const loadTab = useCallback(
    async (which: Tab, signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (which === "overview") {
          setDashboard(await brokerApi.dashboard(accessToken, signal));
        } else if (which === "margins") {
          setMargins(await brokerApi.margins(accessToken, signal));
        } else if (which === "drivers") {
          setRoster(await brokerApi.roster(accessToken, signal));
        } else if (which === "reports") {
          const to = new Date();
          const from = new Date(to);
          from.setDate(from.getDate() - reportDays);
          setReport(
            await brokerApi.reports(
              accessToken,
              { fromDate: isoDate(from), toDate: isoDate(to) },
              signal,
            ),
          );
        }
      } catch (err) {
        if (signal?.aborted === true) {
          return;
        }
        setError(
          err instanceof ApiError ? err.message : "Could not load broker data.",
        );
      } finally {
        if (signal?.aborted !== true) {
          setLoading(false);
        }
      }
    },
    [accessToken, reportDays],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadTab(tab, controller.signal);
    return () => { controller.abort(); };
  }, [tab, loadTab]);

  async function searchNearby(): Promise<void> {
    if (accessToken === null) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setNearby(
        await brokerApi.nearbyVehicles(accessToken, {
          lat: Number(lat),
          lng: Number(lng),
          radiusKm: Number(radiusKm),
        }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not search vehicles.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <AppHeader title="Broker desk" showOwnerSwitcher={false} />

      <main className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-4 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <Button
              key={t.id}
              variant={tab === t.id ? "default" : "outline"}
              size="sm"
              onClick={() => { setTab(t.id); }}
            >
              {t.label}
            </Button>
          ))}
        </div>

        {error !== null ? (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : null}

        {/* ---- AG-01 / AG-02 ---------------------------------------- */}
        {tab === "overview" && dashboard !== null ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Total loads" value={dashboard.total_loads} />
              <StatCard label="Active" value={dashboard.active_loads} />
              <StatCard label="Completed" value={dashboard.completed_loads} />
              <StatCard label="Postponed" value={dashboard.postponed_loads} />
            </div>

            <div className="rounded-lg border bg-background p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                This month
              </p>
              <p className="mt-1 flex items-center gap-2 text-2xl font-semibold">
                <IndianRupee className="h-5 w-5" />
                {rupees(dashboard.month_to_date_net_margin_inr)}
                <span className="text-sm font-normal text-muted-foreground">
                  net margin across {dashboard.month_to_date_load_count} load
                  {dashboard.month_to_date_load_count === 1 ? "" : "s"}
                </span>
              </p>
              {/*
                Surfaced on the overview rather than buried in the margins
                tab: a healthy total can hide several loads sold below cost,
                and those are the ones worth opening today.
              */}
              {dashboard.month_to_date_loss_making_count > 0 ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  {dashboard.month_to_date_loss_making_count} load
                  {dashboard.month_to_date_loss_making_count === 1 ? "" : "s"}{" "}
                  sold below cost — check the Margins tab.
                </p>
              ) : null}
              <p className="mt-2 text-xs text-muted-foreground">
                Net of both the driver payout and the platform commission.
              </p>
            </div>

            <div className="rounded-lg border bg-background p-4">
              <p className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-muted-foreground" />
                {dashboard.roster_driver_count} driver
                {dashboard.roster_driver_count === 1 ? "" : "s"} on your roster
              </p>
            </div>
          </div>
        ) : null}

        {/* ---- AG-07 / AG-08 ---------------------------------------- */}
        {tab === "margins" && margins !== null ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Customer receipts"
                value={rupees(margins.customer_receipts_inr)}
              />
              <StatCard
                label="Driver payouts"
                value={rupees(margins.driver_payouts_inr)}
              />
              <StatCard
                label="Platform commission"
                value={rupees(margins.platform_commission_inr)}
              />
              <StatCard
                label="Net margin"
                value={rupees(margins.net_margin_inr)}
                tone={
                  Number(margins.net_margin_inr) < 0 ? "bad" : "good"
                }
              />
            </div>

            {margins.items.length === 0 ? (
              <p className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
                No brokered loads yet. Take a load from the marketplace at an
                agreed customer price and it will appear here.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border bg-background">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-muted-foreground">
                    <tr>
                      <th className="p-3">Customer pays</th>
                      <th className="p-3">Driver gets</th>
                      <th className="p-3">Commission</th>
                      <th className="p-3">Net margin</th>
                      <th className="p-3">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {margins.items.map((row) => (
                      <tr
                        key={row.load_id}
                        className="border-b last:border-0"
                      >
                        <td className="p-3">
                          {rupees(row.customer_price_inr)}
                        </td>
                        <td className="p-3">{rupees(row.driver_payout_inr)}</td>
                        <td className="p-3">
                          {rupees(row.platform_commission_inr)}
                        </td>
                        <td
                          className={`p-3 font-medium ${
                            row.is_loss_making ? "text-destructive" : ""
                          }`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {row.is_loss_making ? (
                              <TrendingDown className="h-3.5 w-3.5" />
                            ) : (
                              <TrendingUp className="h-3.5 w-3.5" />
                            )}
                            {rupees(row.net_margin_inr)}
                          </span>
                        </td>
                        <td className="p-3">{row.margin_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {/* ---- AG-05 ------------------------------------------------- */}
        {tab === "drivers" && roster !== null ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {roster.available_count} available · {roster.on_trip_count} on a
              trip · {roster.total} total
            </p>
            {roster.items.length === 0 ? (
              <p className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
                No drivers paired to you yet.
              </p>
            ) : (
              roster.items.map((d) => (
                <div
                  key={d.driver_id}
                  className="flex items-center justify-between gap-4 rounded-lg border bg-background p-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{d.full_name ?? "Unnamed"}</p>
                    <p className="text-sm text-muted-foreground">
                      {d.registration_number ?? "No vehicle"}
                      {d.employment_type !== null
                        ? ` · ${d.employment_type.replace("_", " ")}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/*
                      `on_trip` comes from live trips, not the driver's own
                      availability flag -- a driver who forgot to set
                      themselves Busy is still on a trip, and calling them
                      mid-delivery is the mistake this prevents.
                    */}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        d.on_trip
                          ? "bg-muted text-muted-foreground"
                          : d.availability === "available"
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {d.on_trip
                        ? `On trip (${d.current_trip_status ?? "active"})`
                        : (d.availability ?? "offline")}
                    </span>
                    {d.phone !== null && !d.on_trip ? (
                      <a
                        href={`tel:${d.phone}`}
                        className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2"
                      >
                        <Phone className="h-3.5 w-3.5" /> Call
                      </a>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}

        {/* ---- AG-10 / PR-08 / AG-16 --------------------------------- */}
        {tab === "reports" && report !== null ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {[7, 30, 90].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={reportDays === d ? "default" : "outline"}
                  onClick={() => { setReportDays(d); }}
                >
                  Last {d} days
                </Button>
              ))}
              <span className="text-xs text-muted-foreground">
                vs {report.comparison_from_date} to{" "}
                {report.comparison_to_date}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border bg-background p-4">
                <p className="text-sm text-muted-foreground">Loads</p>
                <p className="text-2xl font-semibold">{report.load_count}</p>
                <DeltaBadge pct={report.load_count_delta_pct} />
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="text-sm text-muted-foreground">Net margin</p>
                <p className="text-2xl font-semibold">
                  {rupees(report.net_margin_inr)}
                </p>
                <DeltaBadge pct={report.net_margin_delta_pct} />
              </div>
              <div className="rounded-lg border bg-background p-4">
                <p className="text-sm text-muted-foreground">
                  Customer receipts
                </p>
                <p className="text-2xl font-semibold">
                  {rupees(report.customer_receipts_inr)}
                </p>
              </div>
              {/* Loss-making loads lead with colour only when there ARE
                  any: a red zero is an alarm about nothing. */}
              <div className="rounded-lg border bg-background p-4">
                <p className="text-sm text-muted-foreground">Loss-making</p>
                <p
                  className={`text-2xl font-semibold ${
                    report.loss_making_count > 0 ? "text-red-600" : ""
                  }`}
                >
                  {report.loss_making_count}
                </p>
                {report.loss_making_count > 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    sold below cost
                  </span>
                ) : null}
              </div>
            </div>

            <div className="rounded-lg border bg-background">
              <p className="border-b p-3 text-sm font-medium">Top lanes</p>
              {report.top_lanes.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  No completed lanes in this period.
                </p>
              ) : (
                <div className="divide-y">
                  {report.top_lanes.map((lane) => (
                    <div
                      key={`${lane.from_district ?? "?"}-${lane.to_district ?? "?"}`}
                      className="flex items-center justify-between gap-4 p-3"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <MapPin
                          className="h-4 w-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <span className="truncate text-sm">
                          {lane.from_district ?? "Unknown"} to{" "}
                          {lane.to_district ?? "Unknown"}
                        </span>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-medium">
                          {rupees(lane.net_margin_inr)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {lane.load_count} load
                          {lane.load_count === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {tab === "nearby" ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-background p-4">
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Latitude</span>
                <input
                  className="w-32 rounded-md border bg-background px-3 py-2"
                  value={lat}
                  onChange={(e) => { setLat(e.target.value); }}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Longitude</span>
                <input
                  className="w-32 rounded-md border bg-background px-3 py-2"
                  value={lng}
                  onChange={(e) => { setLng(e.target.value); }}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted-foreground">Radius (km)</span>
                <input
                  className="w-24 rounded-md border bg-background px-3 py-2"
                  value={radiusKm}
                  onChange={(e) => { setRadiusKm(e.target.value); }}
                />
              </label>
              <Button size="sm" onClick={() => void searchNearby()}>
                <MapPin className="mr-1 h-4 w-4" /> Search
              </Button>
            </div>

            {nearby !== null ? (
              nearby.items.length === 0 ? (
                <div className="rounded-lg border bg-background p-6 text-sm">
                  <p className="font-medium">
                    No available vehicles within {nearby.radius_km} km.
                  </p>
                  {/*
                    AG-16. "None found" is a dead end; a distance is a
                    decision. Offer the expanded search rather than leaving
                    the broker to guess a bigger number.
                  */}
                  {nearby.nearest_outside_radius_km !== null ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-muted-foreground">
                        The nearest one is {nearby.nearest_outside_radius_km} km
                        away.
                      </p>
                      {nearby.suggested_radius_km !== null ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRadiusKm(String(nearby.suggested_radius_km));
                            void searchNearby();
                          }}
                        >
                          <RefreshCcw className="mr-1 h-4 w-4" />
                          Search within {nearby.suggested_radius_km} km
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-2 text-muted-foreground">
                      Nothing within 300 km either. Try a later pickup window.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {nearby.total} vehicle{nearby.total === 1 ? "" : "s"} within{" "}
                    {nearby.radius_km} km, nearest first. Only vehicles that
                    can actually take the job are listed.
                  </p>
                  {nearby.items.map((v) => (
                    <div
                      key={v.vehicle_id}
                      className="flex items-center justify-between gap-4 rounded-lg border bg-background p-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{v.registration_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {v.vehicle_type} · {v.payload_capacity_kg} kg ·{" "}
                          {v.distance_km} km away
                        </p>
                        {v.driver_name !== null ? (
                          <p className="text-sm text-muted-foreground">
                            Driver: {v.driver_name}
                          </p>
                        ) : null}
                      </div>
                      {v.owner_phone !== null ? (
                        <a
                          href={`tel:${v.owner_phone}`}
                          className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2"
                        >
                          <Phone className="h-3.5 w-3.5" /> Call owner
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>
        ) : null}
      </main>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: "good" | "bad";
}): React.JSX.Element {
  return (
    <div className="rounded-lg border bg-background p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-1 text-xl font-semibold ${
          tone === "bad" ? "text-destructive" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export default BrokerPage;
